import React, { useState } from 'react'
import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react'
import { cn } from '@/lib/utils'
import { StepsAttributes } from './steps-extension'
import { RotateCcw, Loader2 } from 'lucide-react'
import { Button } from '@deepguide-ai/ui/button'
import { toast } from 'sonner'
import { useParams } from '@tanstack/react-router'
import { APP_NAME } from '@/lib/env';
interface StepsNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

export function StepsNodeView({
  node,
  editor,
  getPos,
  updateAttributes,
  deleteNode,
  selected,
}: StepsNodeViewProps) {
  const attrs = node.attrs as StepsAttributes
  const [isInitiatingReRecord, setIsInitiatingReRecord] = useState(false)
  const params = useParams({ strict: false })
  
  // Only show re-record button if:
  // 1. We have a stepBlockId (this is a Steps block that can be re-recorded)
  // 2. Editor is not in read-only mode
  // 3. We're in a document context (have docId)
  const docId = (params as any).docId || 
                (window.location.search.match(/docId=([^&]+)/)?.[1])
  const canReRecord = attrs.stepBlockId && editor.isEditable && docId

  const handleReRecord = async () => {
    if (!attrs.stepBlockId || !docId) {
      toast.error('Cannot re-record: missing required information')
      return
    }

    setIsInitiatingReRecord(true)
    
    try {
      // Check if extension is installed
      if (!chrome?.runtime?.id) {
        toast.error(`${APP_NAME} extension is not installed or enabled`)
        setIsInitiatingReRecord(false)
        return
      }

      // Send message to extension to prepare for re-recording
      chrome.runtime.sendMessage(
        { 
          action: 'prepareReRecord',
          data: {
            stepBlockId: attrs.stepBlockId,
            originalDocId: docId,
            // Include current flow ID if available for reference
            referenceFlowId: attrs.flowId
          }
        },
        (response) => {
          if (chrome.runtime.lastError) {
            console.error('Extension communication error:', chrome.runtime.lastError)
            toast.error('Failed to communicate with extension. Please make sure it is installed and enabled.')
            setIsInitiatingReRecord(false)
            return
          }

          if (response?.success) {
            // Extension is ready - it will handle:
            // 1. Opening the first URL in a new tab
            // 2. Starting the recording
            // 3. After recording, redirecting to DocGenerationPage with params
            toast.info(
              'Extension is ready. A new tab will open for re-recording. ' +
              'Complete the flow and it will replace this Steps block.',
              { duration: 5000 }
            )
            
            // The extension will redirect to something like:
            // /workspaces/{workspaceId}/docs/generate?flowId={newFlowId}&replaceStepBlockId={stepBlockId}&originalDocId={docId}
          } else {
            toast.error(response?.error || 'Failed to initiate re-recording')
          }
          setIsInitiatingReRecord(false)
        }
      )
      
      // Set a timeout in case extension doesn't respond
      setTimeout(() => {
        if (isInitiatingReRecord) {
          setIsInitiatingReRecord(false)
          toast.error('Extension did not respond. Please try again.')
        }
      }, 5000)
      
    } catch (error) {
      console.error('Failed to initiate re-recording:', error)
      toast.error('Failed to start re-recording')
      setIsInitiatingReRecord(false)
    }
  }

  return (
    <NodeViewWrapper
      className={cn(
        'relative mt-10 mb-9',
        selected && 'ProseMirror-selectednode'
      )}
      data-steps-id={node.attrs.id || `steps-${Date.now()}`}
      data-flow-id={attrs.flowId}
      data-step-block-id={attrs.stepBlockId}
    >
      <section className='steps-container relative'>
        {canReRecord && (
          <div className="absolute -top-8 right-0 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReRecord}
              disabled={isInitiatingReRecord}
              title="Re-record this entire flow from the beginning"
              className="transition-opacity hover:opacity-100 opacity-70"
            >
              {isInitiatingReRecord ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Initiating...
                </>
              ) : (
                <>
                  <RotateCcw className="h-3 w-3" />
                  Re-record Flow
                </>
              )}
            </Button>
          </div>
        )}
        <NodeViewContent />
      </section>
    </NodeViewWrapper>
  )
}